import { TextField } from 'mui-rff';
import React from 'react';
import { z } from 'zod';

import Form from '@dashboard/components/form/Form';
import { alertError, alertSuccess, getApolloErrorMessage } from '@dashboard/contexts/SnackbarManager/store';
import {
  FVerifiedEmailAddressFragment,
  useUpdateVerifiedEmailAddressMutation,
  useVerifiedEmailAddressesQuery,
} from '@dashboard/generated/wp/platform';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';

import SelectDomainField from '../CreateVerifiedEmailAddressDialog/SelectDomainField';

type Props = {
  model: FVerifiedEmailAddressFragment;
  onClose: () => void;
};

const FORM_SCHEMA = z.object({
  name: z.string().nullish(),
  username: z.string(),
  domainNameVerificationId: z.string(),
});

export default function FormDialog({ model, onClose }: Props) {
  const [updateSenderAddress, { loading }] = useUpdateVerifiedEmailAddressMutation();
  const { refetch } = useVerifiedEmailAddressesQuery();

  const onSubmit = async (formData: unknown) => {
    try {
      const d = FORM_SCHEMA.parse(formData);
      await updateSenderAddress({
        variables: {
          id: model.id,
          name: d.name,
          username: d.username,
          domainNameVerificationId: d.domainNameVerificationId,
        },
      });
      await refetch();
      alertSuccess('Approved sender updated');
      onClose();
    } catch (e) {
      const message = getApolloErrorMessage(e) ?? 'Failed to update approved sender';
      alertError(message);
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Edit approved sender</DialogTitle>
      <Form
        initialValues={{
          name: model.name || '',
          username: model.username,
          domainNameVerificationId: model.domainNameVerificationId,
        }}
        onSubmit={onSubmit}
      >
        <DialogContent>
          <Stack spacing={2} pt={2}>
            <TextField autoFocus label="Name" name="name" placeholder="Jane Smith" />
            <TextField
              label="Email address"
              placeholder="jane"
              name="username"
              InputProps={{
                endAdornment: (
                  <>
                    @<SelectDomainField name="domainNameVerificationId" />
                  </>
                ),
              }}
              margin="normal"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={loading} variant="contained" type="submit">
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
}
